import React, { useMemo } from 'react';

import { MultipleSelectModal } from '@bringg-frontend/multiple-select-modal';
import { BringgFontIcons } from '@bringg/bringg-icons';
import { Task } from '@bringg/types';
import { observer } from 'mobx-react';

import { useMonitoringTranslations, useRoutesPlannerManualValidation } from '../../hooks';

export enum ActionKey {
	cancelTask = 'cancelTask',
	userAssigner = 'userAssigner',
	routesPlanner = 'routesPlanner',
	vehicleAssigner = 'vehicleAssigner'
}

interface Props {
	tasks: Task[];
	color?: string;
	clearSelectedTasks(): void;
	toggleModal(key: string, isOpen: boolean): void;
	selectMultipleTasks: (tasks: Task[], shouldSelect: boolean) => void;
}

const MultipleSelectionActions = ({ tasks, clearSelectedTasks, color, toggleModal, selectMultipleTasks }: Props) => {
	const { routesPlanner, createRoute, cancelOrder, multiSelectOrders, moreThenTeamError, notSameExecuteError } =
		useMonitoringTranslations();

	const { getRoutesPlannerManuallyOpenError, addRouteUnselectedTasks } =
		useRoutesPlannerManualValidation(selectMultipleTasks);

	const routesPlannerManuallyOpenError = useMemo(() => {
		return getRoutesPlannerManuallyOpenError(tasks);
	}, [getRoutesPlannerManuallyOpenError, tasks]);

	const onOpenRoutesPlanner = async () => {
		await addRouteUnselectedTasks(tasks);
		toggleModal(ActionKey.routesPlanner, true);
	};

	const getCreateRouteTooltip = (selectedTasks: Task[]): string => {
		const tasksTeam = selectedTasks[0].team_ids?.[0];
		const isDispatchTask = selectedTasks[0].ready_to_execute;

		const isMoreThenOneTeam = selectedTasks.some(task => task.team_ids?.[0] !== tasksTeam);
		if (isMoreThenOneTeam) {
			return moreThenTeamError;
		}
		const isNotSameExecuteStatus = selectedTasks.some(task => task.ready_to_execute !== isDispatchTask);
		if (isNotSameExecuteStatus) {
			return notSameExecuteError;
		}
		return '';
	};

	const multipleSelectButtons = useMemo(() => {
		const createRouteTooltip = getCreateRouteTooltip(tasks);

		const buttons = [
			{
				icon: BringgFontIcons.Route,
				text: createRoute,
				onClick: () => toggleModal(ActionKey.userAssigner, true),
				dataTestId: 'multiple-select-create-route',
				disabled: !!createRouteTooltip,
				tooltip: createRouteTooltip
			},
			{
				icon: BringgFontIcons.Erase,
				text: cancelOrder,
				onClick: () => {
					toggleModal(ActionKey.cancelTask, true);
				},
				dataTestId: 'multiple-select-cancel-orders'
			},
			{
				icon: BringgFontIcons.Planning,
				text: routesPlanner,
				onClick: onOpenRoutesPlanner,
				dataTestId: 'open-in-routes-planner',
				disabled: !!routesPlannerManuallyOpenError,
				tooltip: routesPlannerManuallyOpenError
			}
		];

		return buttons;
	}, [tasks]);

	return (
		<MultipleSelectModal
			selectedCount={tasks.length}
			selectedTypeName={multiSelectOrders(tasks.length)}
			color={color}
			actionButtons={multipleSelectButtons}
			onCloseModal={clearSelectedTasks}
		/>
	);
};

export default observer(MultipleSelectionActions);
